import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  CreditCard,
  Key,
  Monitor,
  IndianRupee,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  ShieldCheck,
  UserPlus,
  RefreshCw,
  FileText,
  Lock,
} from "lucide-react";
import { useMasterAdmin } from "../../context/MasterAdminAuthContext";
import { toast } from "sonner";

export default function MasterAdminDashboard() {
  const { masterApi } = useMasterAdmin();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = async () => {
    try {
      setRefreshing(true);
      const res = await masterApi.get("/master-admin/dashboard/stats");
      setStats(res.data);
    } catch (err) {
      toast.error("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-zinc-400">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-xs">Loading Master Console metrics...</span>
        </div>
      </div>
    );
  }

  const kpis = [
    {
      title: "Total Registered Users",
      value: stats?.total_users ?? 0,
      sub: `${stats?.active_users ?? 0} active, ${stats?.blocked_users ?? 0} blocked`,
      icon: Users,
      color: "from-blue-500/20 to-blue-600/5",
      border: "border-blue-500/30",
      textColor: "text-blue-400",
      link: "/master-admin/users",
    },
    {
      title: "Active Memberships",
      value: stats?.active_memberships ?? 0,
      sub: `₹99 / mo per active PC`,
      icon: CreditCard,
      color: "from-emerald-500/20 to-emerald-600/5",
      border: "border-emerald-500/30",
      textColor: "text-emerald-400",
      link: "/master-admin/memberships",
    },
    {
      title: "Expiring Soon (<=7 Days)",
      value: stats?.expiring_soon ?? 0,
      sub: `${stats?.expired_memberships ?? 0} expired`,
      icon: Clock,
      color: stats?.expiring_soon > 0 ? "from-amber-500/20 to-amber-600/5" : "from-zinc-500/20 to-zinc-600/5",
      border: stats?.expiring_soon > 0 ? "border-amber-500/40" : "border-zinc-800",
      textColor: stats?.expiring_soon > 0 ? "text-amber-400" : "text-zinc-400",
      link: "/master-admin/memberships",
    },
    {
      title: "Active PC Licenses",
      value: stats?.active_licenses ?? 0,
      sub: `${stats?.registered_devices ?? 0} hardware bound PCs`,
      icon: Key,
      color: "from-purple-500/20 to-purple-600/5",
      border: "border-purple-500/30",
      textColor: "text-purple-400",
      link: "/master-admin/licenses",
    },
    {
      title: "Monthly Revenue (Recorded)",
      value: `₹${stats?.monthly_revenue?.toLocaleString("en-IN") || 0}`,
      sub: "Active subscriber base",
      icon: IndianRupee,
      color: "from-amber-500/20 to-amber-600/5",
      border: "border-amber-500/30",
      textColor: "text-amber-400",
      link: "/master-admin/payments",
    },
    {
      title: "Bound Hardware Devices",
      value: stats?.registered_devices ?? 0,
      sub: "1 PC = 1 Active License",
      icon: Monitor,
      color: "from-cyan-500/20 to-cyan-600/5",
      border: "border-cyan-500/30",
      textColor: "text-cyan-400",
      link: "/master-admin/devices",
    },
  ];

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto pb-10">
      {/* Title & Top Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-white tracking-tight flex items-center gap-2">
            <span>Master Dashboard</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 font-bold">
              Live Monitor
            </span>
          </h1>
          <p className="text-xs text-zinc-400 mt-0.5">
            Centralized subscriber, license, and revenue operations for PrintQ.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchStats}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-semibold text-zinc-300 hover:text-white transition-all active:scale-95"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin text-amber-400" : ""}`} />
            <span>Refresh</span>
          </button>
          <Link
            to="/master-admin/users"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:brightness-110 active:scale-95 text-zinc-950 font-bold text-xs shadow-md shadow-amber-500/20 transition-all"
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span>Create New User</span>
          </Link>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <Link
              key={idx}
              to={kpi.link}
              className={`bg-gradient-to-br ${kpi.color} bg-zinc-900/90 border ${kpi.border} rounded-2xl p-5 shadow-lg shadow-black/40 hover:scale-[1.01] transition-all flex flex-col justify-between group`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-zinc-300">{kpi.title}</span>
                <div className={`w-8 h-8 rounded-xl bg-zinc-950/80 border ${kpi.border} flex items-center justify-center ${kpi.textColor}`}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
              <div className="mt-4">
                <div className="text-2xl font-black text-white tracking-tight">{kpi.value}</div>
                <div className="text-[11px] text-zinc-400 mt-1 flex items-center justify-between">
                  <span>{kpi.sub}</span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-amber-400 transition-colors" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Main 2-Column Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Subscription Growth & Expiring Memberships */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Expiring Soon Table */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 shadow-xl flex flex-col gap-4">
            <div className="flex items-center justify-between border-b border-zinc-800/80 pb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-400" />
                <h2 className="text-sm font-bold text-white">Upcoming Expirations</h2>
              </div>
              <Link
                to="/master-admin/memberships"
                className="text-xs font-semibold text-amber-400 hover:text-amber-300 flex items-center gap-1"
              >
                <span>View All</span>
                <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>

            {stats?.expiring_soon_list?.length === 0 ? (
              <div className="py-8 text-center text-xs text-zinc-500 flex flex-col items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                <span>No memberships expiring within the next 7 days.</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="text-zinc-500 border-b border-zinc-800">
                      <th className="pb-2 font-medium">Username</th>
                      <th className="pb-2 font-medium">Plan</th>
                      <th className="pb-2 font-medium">Expiry Date</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/60">
                    {stats?.expiring_soon_list?.map((mem) => (
                      <tr key={mem.membership_id} className="hover:bg-zinc-800/30">
                        <td className="py-3 font-semibold text-white font-mono">{mem.username}</td>
                        <td className="py-3 text-zinc-400">{mem.plan_name}</td>
                        <td className="py-3 text-amber-400 font-mono">
                          {new Date(mem.expiry_date).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td className="py-3">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                            Expiring Soon
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <Link
                            to={`/master-admin/users`}
                            className="px-2.5 py-1 rounded-md bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold text-[11px] transition-colors"
                          >
                            Renew
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Monthly Revenue Trend Bars */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 shadow-xl flex flex-col gap-4">
            <div className="flex items-center justify-between border-b border-zinc-800/80 pb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <h2 className="text-sm font-bold text-white">Monthly Subscription Trend</h2>
              </div>
              <span className="text-xs text-zinc-500">Last 4 Months</span>
            </div>

            <div className="grid grid-cols-4 gap-3 pt-2">
              {stats?.trend?.map((item, i) => (
                <div key={i} className="bg-zinc-950/60 border border-zinc-800/80 rounded-xl p-3 flex flex-col items-center text-center gap-1">
                  <span className="text-[11px] font-semibold text-zinc-400">{item.month}</span>
                  <div className="text-base font-bold text-white">{item.users} users</div>
                  <span className="text-[10px] font-mono text-emerald-400">₹{item.revenue}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Col: Quick Admin Controls & Policy Summary */}
        <div className="flex flex-col gap-6">
          {/* Licensing Rules Summary */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 shadow-xl flex flex-col gap-4">
            <div className="flex items-center gap-2 border-b border-zinc-800 pb-3">
              <ShieldCheck className="w-4 h-4 text-amber-400" />
              <h2 className="text-sm font-bold text-white">License & Grace Policy</h2>
            </div>

            <div className="flex flex-col gap-3 text-xs text-zinc-300 leading-relaxed">
              <div className="bg-zinc-950/80 rounded-xl p-3 border border-zinc-800/80 flex flex-col gap-1">
                <span className="text-amber-400 font-bold">1 Active PC = 1 License</span>
                <p className="text-[11px] text-zinc-400">
                  Each user account is securely bound to 1 hardware machine fingerprint. Second PC logins are rejected until the Master Admin unbinds the previous device.
                </p>
              </div>

              <div className="bg-zinc-950/80 rounded-xl p-3 border border-zinc-800/80 flex flex-col gap-1">
                <span className="text-emerald-400 font-bold">₹99 / Month Membership</span>
                <p className="text-[11px] text-zinc-400">
                  Standard 30-day billing cycle. Automatic grace period of 7 days upon expiration to prevent unexpected business disruption for Xerox/Digital shops.
                </p>
              </div>

              <div className="bg-zinc-950/80 rounded-xl p-3 border border-zinc-800/80 flex flex-col gap-1">
                <span className="text-blue-400 font-bold">Customer Support Lead</span>
                <p className="text-[11px] text-zinc-400">
                  Kunal Warkante (+91 9545262332) handles manual unbinding requests, renewals, and onboarding.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Action Links */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 shadow-xl flex flex-col gap-3">
            <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Quick Management</h3>
            <div className="flex flex-col gap-2">
              <Link
                to="/master-admin/users"
                className="flex items-center justify-between p-2.5 rounded-xl bg-zinc-950/80 hover:bg-zinc-800 border border-zinc-800/80 text-xs font-semibold text-zinc-200 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span>Manage User Accounts</span>
                </div>
                <ArrowUpRight className="w-3.5 h-3.5 text-zinc-500" />
              </Link>
              <Link
                to="/master-admin/licenses"
                className="flex items-center justify-between p-2.5 rounded-xl bg-zinc-950/80 hover:bg-zinc-800 border border-zinc-800/80 text-xs font-semibold text-zinc-200 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <Key className="w-4 h-4 text-purple-400" />
                  <span>Unbind / Transfer Licenses</span>
                </div>
                <ArrowUpRight className="w-3.5 h-3.5 text-zinc-500" />
              </Link>
              <Link
                to="/master-admin/audit-logs"
                className="flex items-center justify-between p-2.5 rounded-xl bg-zinc-950/80 hover:bg-zinc-800 border border-zinc-800/80 text-xs font-semibold text-zinc-200 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <FileText className="w-4 h-4 text-amber-400" />
                  <span>View System Audit Trail</span>
                </div>
                <ArrowUpRight className="w-3.5 h-3.5 text-zinc-500" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
